// ¿HAY UNA VERSIÓN NUEVA DE LA APP EN EL SERVIDOR?
//
// EL PROBLEMA, con las palabras del dueño (2-sep-2026): «cambia el sw o lo que sea para que le
// llegue a todas las clinicas, ya que si no entras en incognito, no se ve bien». Cada arreglo que
// subimos no le llegaba: seguía viendo la versión de antes salvo en una ventana de incógnito.
//
// Y NO ERA EL SERVICE WORKER: `public/sw.js` no tiene ni un `addEventListener('fetch')`, o sea que
// no cachea NADA — solo sirve para las notificaciones. Lo comprobé antes de tocarlo.
//
// EL CULPABLE ES `index.html`. Vite pone un hash en el nombre de cada .js y .css, así que esos se
// renuevan solos… PERO solo si el navegador vuelve a leer el `index.html`, que es quien los nombra.
// Si ese fichero se sirve cacheado, el navegador sigue pidiendo el bundle VIEJO por su nombre viejo
// —que existe y responde 200— y la app se queda congelada en la versión del día que la abriste. En
// incógnito no hay caché, y por eso ahí sí se veía lo nuevo.
//
// LO SUYO ES ARREGLARLO EN EL SERVIDOR (nginx: `index.html` con `no-cache`, los assets con hash a un
// año). Esto de aquí es el cinturón del lado del navegador, y hace falta igual: con cientos de
// pestañas abiertas semanas enteras en las clínicas, nadie recarga a mano.
//
// CÓMO. Se pide `index.html` SIN caché y se mira con qué bundle se llama a sí mismo. Si no es el que
// esta pestaña tiene cargado, hay versión nueva. No se recarga a la brava: recargar a alguien que
// está escribiendo una nota de un paciente le borra lo escrito. Se avisa, y quien decide es la
// persona — salvo que la pestaña lleve un buen rato sin tocarse, que entonces no hay nada que
// perder.
import { useEffect, useState } from 'react'

// Cada cuánto se pregunta. No es gratis pero casi: una petición pequeña, sin caché, cada 10 minutos.
const CADA = 10 * 60 * 1000
// Inactividad a partir de la cual se recarga sin preguntar: la pestaña está abierta pero nadie la
// está usando, así que no se puede perder nada escrito a medias.
const OLVIDADA = 15 * 60 * 1000

/** El nombre del bundle que ESTA pestaña tiene cargado. */
function bundleActual() {
  try {
    const s = [...document.querySelectorAll('script[type="module"][src]')]
      .map((x) => x.getAttribute('src'))
      .find((x) => x && x.includes('/assets/'))
    return s || null
  } catch (_) { return null }
}

/** El que anuncia el servidor AHORA MISMO. null si no se ha podido leer (offline, 502, despliegue). */
async function bundleDelServidor() {
  try {
    // `no-store` en los dos sitios: la opción de fetch Y la cabecera. Sin esto, la comprobación de
    // si hay algo nuevo se contestaría… desde la caché, que es justo lo que se está midiendo.
    const r = await fetch('/index.html?v=' + Date.now(), { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } })
    if (!r.ok) return null
    const html = await r.text()
    // `[^"]*` y no `[^"]+`: Vite escribe la ruta del paquete empezando justo por la carpeta assets, sin nada delante, y con
    // `+` no casaba NUNCA: el aviso no salía y la pestaña olvidada no se recargaba (t298, 19-oct-2026).
    const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]*\/assets\/[^"]+)"/)
    return m ? m[1] : null
  } catch (_) { return null }
}

// Devuelve el paquete NUEVO que anuncia el servidor (una cadena, «hay versión nueva»), o null mientras no lo haya. Antes era
// un sí/no; ahora el aviso sabe de qué versión es lo que se cerró y vuelve a salir si llega otra (t315, 20-oct-2026).
export function useVersionNueva() {
  const [nueva, setNueva] = useState(null)

  useEffect(() => {
    const mio = bundleActual()
    // Sin bundle con hash (desarrollo con Vite en caliente) no hay nada que comparar.
    if (!mio) return undefined
    let vivo = true
    let ultimaActividad = Date.now()
    const toco = () => { ultimaActividad = Date.now() }
    for (const ev of ['pointerdown', 'keydown', 'visibilitychange']) window.addEventListener(ev, toco, { passive: true })

    // t96 · LO QUE SE SABE DE LA VERSIÓN DE ESTA PESTAÑA, para el globo de ayuda: al escribir al soporte se manda el paquete
    // que tiene cargado y si hay uno más nuevo en el servidor (null = aún no se ha mirado). Va en `window` y no en un import
    // porque las guardias montan el globo (components/ChatWidget) con sus dobles.
    const anotar = (desactualizada) => { try { window.__biangoVersion = { paquete: mio, desactualizada } } catch (_) { /* sin ventana */ } }
    anotar(null)
    const mirar = async () => {
      const suyo = await bundleDelServidor()
      if (vivo && suyo) anotar(suyo !== mio)
      if (!vivo || !suyo || suyo === mio) return
      // Hay versión nueva. Si nadie ha tocado esta pestaña en un buen rato, se recarga sola: no hay
      // nada a medias que perder y así la clínica amanece con la versión buena sin hacer nada.
      if (Date.now() - ultimaActividad > OLVIDADA) { window.location.reload(); return }
      setNueva(suyo)
    }
    // La primera, al arrancar: es cuando más barato sale recargar (aún no se ha hecho nada).
    const t0 = setTimeout(mirar, 4000)
    const t = setInterval(mirar, CADA)
    return () => {
      vivo = false
      clearTimeout(t0); clearInterval(t)
      for (const ev of ['pointerdown', 'keydown', 'visibilitychange']) window.removeEventListener(ev, toco)
    }
  }, [])

  return nueva
}
